import React, { useMemo, ReactDOM, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, IconButton, Toolbar, useMediaQuery } from '@mui/material';
import { KeyboardArrowLeftOutlined, KeyboardArrowRightOutlined } from '@mui/icons-material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';

// assets
import { IconChevronRight } from '@tabler/icons';

import bgImg from 'assets/images/bg-img.png';
import { borderRadius, maxHeight } from '@mui/system';

import { usePermissions } from 'contexts/PermissionContext';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('lg')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('lg')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    const location = useLocation();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));
    const matchDownLg = useMediaQuery(theme.breakpoints.down('xl'));
    const matchUpLg = useMediaQuery(theme.breakpoints.up('xl'));

    const dispatch = useDispatch();
    const { container } = useConfig();

    const regex = /^[/]w[/][0-9a-zA-Z-]*[/]p[/][0-9a-zA-Z-]*[/]c-d[/][0-9a-zA-Z-]*/gi;

    const [limitedHeight, setLimitedHeight] = React.useState(true);
    const [drawerOpen, setDrawerOpen] = React.useState(true);
    const { refreshPermissions } = usePermissions();

    React.useEffect(() => {
        setLimitedHeight(true);
        if (regex.test(location.pathname)) {
            setLimitedHeight(false);
        }
        refreshPermissions();
    }, [location]);

    useEffect(() => {
        if (localStorage.getItem('drawerOpen') === undefined || localStorage.getItem('drawerOpen') === null) {
            console.log('param not found from local storage');
            localStorage.setItem('drawerOpen', true);
        } else {
            const value = JSON.parse(localStorage.getItem('drawerOpen'));
            setDrawerOpen(value);
            dispatch(openDrawer(drawerOpen));
        }
    }, []);

    const header = useMemo(
        () => (
            <Toolbar>
                <Header />
            </Toolbar>
        ),
        []
    );

    const handleToggleDrawer = () => {
        const newStatus = !drawerOpen;
        setDrawerOpen(newStatus);
        localStorage.setItem('drawerOpen', newStatus);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                backgroundImage: `url(${bgImg})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                height: limitedHeight ? '100vh' : 'unset',
                maxHeight: limitedHeight? '100vh' : 'unset',
                minHeight: '100vh'
            }}
        >
            <CssBaseline />

            {/* drawer */}
            <Sidebar drawerOpen={drawerOpen} handleToggleDrawer={handleToggleDrawer} />

            {/* main content */}
            <Main
                theme={theme}
                open={drawerOpen}
                sx={{
                    bgcolor: '#FFFFFF4D',
                    border: '1px solid #FFFFFF',
                    borderRadius: '24px',
                    margin: '20px',
                    padding: '40px !important',
                    height: limitedHeight ? '100%' : 'unset',
                    maxHeight: limitedHeight ? 'calc(100vh - 40px)' : 'unset',
                    minHeight: 'unset',
                    position: 'relative'
                }}
            >
                <IconButton
                    aria-label="drawer collapse and expand"
                    sx={{
                        position: !limitedHeight ? 'fixed' : 'absolute',
                        top: !limitedHeight ? '52px' : '32px',
                        left: (!limitedHeight && !drawerOpen) ? '0px' : (!limitedHeight && drawerOpen) ? '260px' : '-20px',
                        bgcolor: '#FFFFFF',
                        '&:hover': {
                            bgcolor: '#FFFFFF'
                        }
                    }}
                    onClick={handleToggleDrawer}
                >
                    {drawerOpen && <KeyboardArrowLeftOutlined />}
                    {!drawerOpen && <KeyboardArrowRightOutlined />}
                </IconButton>
                {/* breadcrumb */}
                <Outlet />
            </Main>
        </Box>
    );
};

export default MainLayout;
